// src/features/admin/roleMasterApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Types for Role Master
export interface Role {
  id: number;
  role_name: string;
  role_desc: string | null;
  createddt: string;
  updateddt: string | null;
}

export interface AddRoleRequest {
  role_name: string;
  role_desc: string;
}

export interface AddRoleResponse {
  error: boolean;
  message: string;
  recId?: number;
}

export interface ListRolesResponse {
  error: boolean;
  message: string;
  roles?: Role[];
}

export interface UpdateRoleRequest {
  id: number;
  role_name: string;
  role_desc: string;
}

export interface UpdateRoleResponse {
  error: boolean;
  message: string;
}

export interface DeleteRoleResponse {
  error: boolean;
  message: string;
}

export const ROLE_MASTER_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarsetech.com',
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
  ENDPOINTS: {
    ADD_ROLE: '/admin/add_role',
    LIST_ROLES: '/admin/list_roles',
    UPDATE_ROLE: '/admin/update_role',
    DELETE_ROLE: '/admin/delete_role',
  },
};

export const roleMasterApi = createApi({
  reducerPath: 'roleMasterApi',
  baseQuery: fetchBaseQuery({
    baseUrl: ROLE_MASTER_API_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');
      headers.set('authkey', ROLE_MASTER_API_CONFIG.AUTH_KEY);
      
      // Add admin token if available
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      return headers;
    },
  }),
  tagTypes: ['Roles'],
  endpoints: (build) => ({
    // Add Role
    addRole: build.mutation<AddRoleResponse, AddRoleRequest>({
      query: (data) => {
        console.log('Add Role Data:', data);
        
        return {
          url: ROLE_MASTER_API_CONFIG.ENDPOINTS.ADD_ROLE,
          method: 'POST',
          body: JSON.stringify({
            role_name: data.role_name.trim(),
            role_desc: data.role_desc.trim(),
          }),
        };
      },
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('Add Role Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role added successfully',
          recId: response.recId,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('Add Role Error:', response);
        
        if (response.status === 409) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Role already exists' 
            }
          };
        } else if (response.status === 400) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: response.data?.message || 'Invalid data provided' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to add role. Please try again.' 
          }
        };
      },
    }),

    // List Roles
    listRoles: build.query<ListRolesResponse, void>({
      query: () => {
        console.log('Fetching Roles List');
        
        return {
          url: ROLE_MASTER_API_CONFIG.ENDPOINTS.LIST_ROLES,
          method: 'POST',
          body: JSON.stringify({
            role_name: "admin 12"
          }),
        };
      },
      providesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('List Roles Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role List',
          roles: response.roles || [],
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('List Roles Error:', response);
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to fetch roles. Please try again.' 
          }
        };
      },
    }),

    // Update Role
    updateRole: build.mutation<UpdateRoleResponse, UpdateRoleRequest>({
      query: (data) => {
        console.log('Update Role Data:', data);
        
        return {
          url: ROLE_MASTER_API_CONFIG.ENDPOINTS.UPDATE_ROLE,
          method: 'POST',
          body: JSON.stringify({
            id: data.id,
            role_name: data.role_name.trim(),
            role_desc: data.role_desc.trim(),
          }),
        };
      },
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('Update Role Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role updated successfully',
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('Update Role Error:', response);
        
        if (response.status === 404) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Role not found' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to update role. Please try again.' 
          }
        };
      },
    }),

    // Delete Role
    deleteRole: build.mutation<DeleteRoleResponse, number>({
      query: (id) => {
        console.log('Delete Role ID:', id);
        
        return {
          url: ROLE_MASTER_API_CONFIG.ENDPOINTS.DELETE_ROLE,
          method: 'POST',
          body: JSON.stringify({
            id: id,
          }),
        };
      },
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => {
        console.log('Delete Role Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Role deleted successfully',
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('Delete Role Error:', response);
        
        if (response.status === 404) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Role not found' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Failed to delete role. Please try again.' 
          }
        };
      },
    }),
  }),
});

export const {
  useAddRoleMutation,
  useListRolesQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = roleMasterApi;